/**
 * Mini-map: a small 2D overview of the board with the current viewport marked; click jumps the camera
 * there. [P5-minimap] — carried from the legacy repo's intent (`.planning/research/old-repo-intent.md`).
 */

import type { Pos } from '@carc/engine';
import { TILE_SIZE, worldPosition } from './tile-mesh.js';
import type { Scene } from './scene.js';

export type TileKind = 'city' | 'road' | 'cloister' | 'field';

interface Entry {
  readonly pos: Pos;
  readonly kind: TileKind;
}

const KIND_COLOUR: Record<TileKind, string> = {
  city: '#b5905e',
  road: '#c9b083',
  cloister: '#e9ddc2',
  field: '#6d9a4c',
};

const PADDING_TILES = 3;

export class MiniMap {
  readonly element: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private entries: Entry[] = [];
  private size = 0;
  private minX = 0;
  private minZ = 0;
  private scalePxPerUnit = 1;

  constructor(
    private readonly scene: Scene,
    private readonly onJump: (world: { x: number; z: number }) => void,
  ) {
    this.element = document.createElement('div');
    this.element.className = 'hud-minimap panel';
    this.element.setAttribute('role', 'button');
    this.element.setAttribute('aria-label', 'Board overview — click to jump the camera');
    this.element.tabIndex = 0;

    this.canvas = document.createElement('canvas');
    this.canvas.width = 260;
    this.canvas.height = 260;
    this.element.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d') as CanvasRenderingContext2D;

    this.canvas.addEventListener('pointerdown', (event) => this.handleClick(event));
    this.element.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') this.jumpToCenter();
    });
  }

  setTiles(entries: readonly Entry[]): void {
    this.entries = entries.slice();
    this.render();
  }

  tick(): void {
    this.render();
  }

  private computeTransform(): void {
    let minX = -PADDING_TILES;
    let maxX = PADDING_TILES;
    let minZ = -PADDING_TILES;
    let maxZ = PADDING_TILES;
    for (const entry of this.entries) {
      const [x, , z] = worldPosition(entry.pos);
      minX = Math.min(minX, x - PADDING_TILES);
      maxX = Math.max(maxX, x + PADDING_TILES);
      minZ = Math.min(minZ, z - PADDING_TILES);
      maxZ = Math.max(maxZ, z + PADDING_TILES);
    }
    const spanX = maxX - minX;
    const spanZ = maxZ - minZ;
    this.size = this.canvas.width;
    this.scalePxPerUnit = this.size / Math.max(spanX, spanZ, 1);
    // Centre the (possibly non-square) world extent within the square canvas.
    this.minX = minX - (Math.max(spanX, spanZ) - spanX) / 2;
    this.minZ = minZ - (Math.max(spanX, spanZ) - spanZ) / 2;
  }

  private worldToCanvas(x: number, z: number): [number, number] {
    return [(x - this.minX) * this.scalePxPerUnit, (z - this.minZ) * this.scalePxPerUnit];
  }

  private canvasToWorld(px: number, py: number): { x: number; z: number } {
    return { x: px / this.scalePxPerUnit + this.minX, z: py / this.scalePxPerUnit + this.minZ };
  }

  private render(): void {
    this.computeTransform();
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.fillStyle = '#241a10';
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    const tilePx = Math.max(2, TILE_SIZE * this.scalePxPerUnit);
    for (const entry of this.entries) {
      const [wx, , wz] = worldPosition(entry.pos);
      const [px, py] = this.worldToCanvas(wx - TILE_SIZE / 2, wz - TILE_SIZE / 2);
      ctx.fillStyle = KIND_COLOUR[entry.kind];
      ctx.fillRect(px, py, tilePx * 0.92, tilePx * 0.92);
    }

    // Viewport rectangle, derived from the camera's distance to its target.
    const target = this.scene.controls.target;
    const distance = this.scene.camera.position.distanceTo(target);
    const halfExtent = distance * 0.62;
    const [rx, ry] = this.worldToCanvas(target.x - halfExtent, target.z - halfExtent);
    const size = halfExtent * 2 * this.scalePxPerUnit;
    ctx.strokeStyle = '#e8dcc0';
    ctx.lineWidth = 2;
    ctx.strokeRect(rx, ry, size, size);
  }

  private handleClick(event: PointerEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    const px = ((event.clientX - rect.left) / rect.width) * this.canvas.width;
    const py = ((event.clientY - rect.top) / rect.height) * this.canvas.height;
    const world = this.canvasToWorld(px, py);
    this.onJump(world);
  }

  private jumpToCenter(): void {
    this.onJump({ x: this.scene.controls.target.x, z: this.scene.controls.target.z });
  }
}
